import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Landmark, UserPlus, Loader2, AlertTriangle, ShieldAlert, FileCheck2, Upload, Download, CheckCircle2, XCircle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ESTADOS_FID, ROLES_PARTE, ROL_PARTE_LABEL, ROLES_FIDEICOMISARIO, CAUSAS_EXTINCION, ESTADO_CIVIL_OPTIONS } from "@/lib/fideicomisos";
import { PatrimonioTab } from "@/components/fideicomisos/PatrimonioTab";
import { CreditosCoberturaTab, CoberturaBadge } from "@/components/fideicomisos/CreditosCoberturaTab";
import { EjecucionTab } from "@/components/fideicomisos/EjecucionTab";
import { ContabilidadTab } from "@/components/fideicomisos/ContabilidadTab";
import { toast } from "sonner";

function EstadoBadge({ estado }) {
  const m = ESTADOS_FID[estado] || { label: estado, cls: "bg-slate-100 text-slate-700" };
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span>;
}

function Requisito({ ok, children }) {
  return (
    <li className="flex items-start gap-2 text-xs">
      {ok ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />}
      <span className={ok ? "text-slate-600" : "text-slate-800 font-medium"}>{children}</span>
    </li>
  );
}

export default function FideicomisoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const esAdmin = user?.role === "admin_entidad";
  const puedePartes = ["admin_entidad", "oficial_cumplimiento"].includes(user?.role);
  const puedeDocs = ["admin_entidad", "juridico"].includes(user?.role);

  const [fid, setFid] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Transición
  const [trans, setTrans] = useState(null); // {nuevo_estado}
  const [motivo, setMotivo] = useState("");
  const [causa, setCausa] = useState("");

  // Alta de parte
  const [alta, setAlta] = useState(false);
  const [busq, setBusq] = useState("");
  const [personas, setPersonas] = useState([]);
  const [sel, setSel] = useState(null);
  const [rol, setRol] = useState("");
  const [pct, setPct] = useState("");
  const [prelacion, setPrelacion] = useState("");
  const [atesta, setAtesta] = useState(false);

  // Estado civil pendiente
  const [ecDialog, setEcDialog] = useState(null); // {persona_id, nombre}
  const [ec, setEc] = useState("");
  const [regimen, setRegimen] = useState("");

  // Documentos
  const [subiendo, setSubiendo] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/fideicomisos/${id}`);
      setFid(data);
    } catch (e) { setError(apiError(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!alta) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/fideicomisos/personas/buscar", { params: { q: busq } });
        setPersonas(data);
      } catch (e) { console.debug(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [alta, busq]);

  const ejecutarTransicion = async () => {
    setBusy(true);
    try {
      await api.post(`/fideicomisos/${id}/transicion`, {
        nuevo_estado: trans, motivo, ...(trans === "extinguido" ? { causa_extincion: causa } : {}),
      });
      toast.success(`Transición aplicada: ${ESTADOS_FID[trans]?.label || trans}.`);
      setTrans(null); setMotivo(""); setCausa("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const registrarParte = async () => {
    if (!sel || !rol) { toast.error("Selecciona la persona y el rol"); return; }
    setBusy(true);
    try {
      const body = { persona_id: sel.id, rol };
      if (pct) body.porcentaje_derechos = pct;
      if (prelacion) body.orden_prelacion = prelacion;
      if (rol === "ejecutor_independiente") body.atestacion_independencia = { declarado: atesta };
      const { data } = await api.post(`/fideicomisos/${id}/partes`, body);
      toast.success(`Parte registrada: ${sel.nombre || sel.razon_social} como ${ROL_PARTE_LABEL[rol]}.`);
      if (data.requiere_estado_civil) {
        setEcDialog({ persona_id: sel.id, nombre: sel.nombre || sel.razon_social });
      }
      setAlta(false); setSel(null); setRol(""); setPct(""); setPrelacion(""); setAtesta(false); setBusq("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const guardarEstadoCivil = async () => {
    if (!ec) { toast.error("Selecciona el estado civil"); return; }
    setBusy(true);
    try {
      await api.post(`/fideicomisos/personas/${ecDialog.persona_id}/estado-civil`, {
        estado_civil: ec, regimen_matrimonial: regimen,
      });
      toast.success("Estado civil guardado en el expediente del cliente (fuente única).");
      setEcDialog(null); setEc(""); setRegimen("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const bajaParte = async (p) => {
    try {
      await api.patch(`/fideicomisos/${id}/partes/${p.id}`, { estatus: p.estatus === "activa" ? "baja" : "activa" });
      toast.success(p.estatus === "activa" ? "Parte dada de baja (baja lógica)." : "Parte reactivada.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const subirDocumento = async (clave, file) => {
    if (!file) return;
    setSubiendo(clave);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", clave === "contrato_fideicomiso" ? "Contrato de fideicomiso" : "Evidencia de registro CONDUSEF (fideicomisos)");
      fd.append("clasificacion", "Fideicomisos");
      fd.append("categoria", "fideicomiso");
      fd.append("referencia_id", id);
      const { data } = await api.post("/documentos/upload", fd);
      await api.post(`/fideicomisos/${id}/documentos`, { documento_id: data.id, tipo_canonico: clave });
      toast.success(`Documento vinculado (${data.nombre_archivo}). SHA-256: ${(data.hash_sha256 || "").slice(0, 12)}…`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubiendo(""); }
  };

  const descargar = async (docId) => {
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      window.open(`${api.defaults.baseURL}${data.url.replace(/^\/api/, "")}`, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error)
    return (
      <div>
        <PageHeader title="Fideicomiso" subtitle="Detalle" />
        <div className="flex flex-col items-center gap-3 p-16 text-slate-400">
          <ShieldAlert className="h-10 w-10" /><p className="max-w-md text-center text-sm">{error}</p>
        </div>
      </div>
    );
  if (!fid) return <div className="p-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  const terminal = ["extinguido", "revertido"].includes(fid.estado);
  const req = fid.requisitos_constitucion;

  return (
    <div>
      <PageHeader title={`${fid.folio} — ${fid.denominacion}`} subtitle="Fideicomiso de garantía (LGTOC 395–407) · Fiduciario: la propia entidad" />
      <div className="space-y-5 p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="fid-volver" variant="outline" size="sm" onClick={() => navigate("/fideicomisos")}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Cartera
          </Button>
          <EstadoBadge estado={fid.estado} />
          <CoberturaBadge semaforo={fid.cobertura?.semaforo} cobertura={fid.cobertura?.cobertura} />
          {fid.causa_extincion && (
            <span className="text-xs text-slate-500">Causa de extinción: {CAUSAS_EXTINCION.find((c) => c.value === fid.causa_extincion)?.label || fid.causa_extincion}</span>
          )}
          <div className="flex-1" />
          {esAdmin && !terminal && (fid.transiciones_validas || []).map((t) => (
            <Button key={t} data-testid={`fid-transicion-${t}`} size="sm"
              variant={t === "extinguido" || t === "revertido" ? "outline" : "default"}
              className={t === "extinguido" || t === "revertido" ? "border-red-200 text-red-600 hover:bg-red-50" : ""}
              onClick={() => { setTrans(t); setMotivo(""); setCausa(""); }}>
              → {ESTADOS_FID[t]?.label || t}
            </Button>
          ))}
        </div>

        {fid.estado === "en_integracion" && req && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="fid-requisitos-constitucion">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-800">Requisitos para constituir</p>
            <ul className="space-y-1.5">
              <Requisito ok={req.fiduciario_ok}>Fiduciario: la propia entidad (créditos propios)</Requisito>
              <Requisito ok={req.fideicomitente_ok}>Al menos 1 fideicomitente activo</Requisito>
              <Requisito ok={req.fideicomisario_ok}>Al menos 1 fideicomisario activo</Requisito>
              <Requisito ok={req.ejecutor_ok}>Ejecutor independiente con atestación aceptada (Art. 395, párr. 2)</Requisito>
              <Requisito ok={req.screening_ok}>
                Screening PLD resuelto de todas las partes{req.problemas_screening?.length ? ` — ${req.problemas_screening.join("; ")}` : ""}
              </Requisito>
              <Requisito ok={req.contrato_ok}>Contrato de fideicomiso cargado (PDF)</Requisito>
              <Requisito ok={req.bienes_ok}>Al menos 1 bien en estado "en patrimonio" (patrimonio fideicomitido)</Requisito>
            </ul>
          </div>
        )}

        <Tabs defaultValue="resumen">
          <TabsList>
            <TabsTrigger data-testid="fid-tab-resumen" value="resumen">Resumen</TabsTrigger>
            <TabsTrigger data-testid="fid-tab-partes" value="partes">Partes ({(fid.partes || []).filter((p) => p.estatus === "activa").length})</TabsTrigger>
            <TabsTrigger data-testid="fid-tab-patrimonio" value="patrimonio">Patrimonio ({(fid.patrimonio || []).filter((b) => b.estado_bien === "en_patrimonio").length})</TabsTrigger>
            <TabsTrigger data-testid="fid-tab-creditos" value="creditos">Créditos y Cobertura ({(fid.vinculos || []).filter((v) => v.estado_vinculo === "activo").length})</TabsTrigger>
            <TabsTrigger data-testid="fid-tab-ejecucion" value="ejecucion">Ejecución</TabsTrigger>
            <TabsTrigger data-testid="fid-tab-contabilidad" value="contabilidad">Contabilidad</TabsTrigger>
            <TabsTrigger data-testid="fid-tab-documentos" value="documentos">Documentos</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="mt-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Datos generales</p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                  <dt className="text-slate-500">Folio</dt><dd className="font-medium text-slate-800">{fid.folio}</dd>
                  <dt className="text-slate-500">Modelo</dt><dd className="font-medium text-slate-800">Créditos propios (SOFOM fiduciaria)</dd>
                  <dt className="text-slate-500">Fecha de constitución</dt><dd className="font-medium text-slate-800">{fid.fecha_constitucion || "—"}</dd>
                  <dt className="text-slate-500">Moneda</dt><dd className="font-medium text-slate-800">{fid.moneda}</dd>
                  <dt className="text-slate-500">Multiobligación</dt><dd className="font-medium text-slate-800">{fid.es_multiobligacion ? "Sí" : "No"}</dd>
                  <dt className="text-slate-500">Honorarios</dt>
                  <dd className="font-medium text-slate-800">
                    Apertura ${Number(fid.honorarios?.monto_apertura || 0).toLocaleString()} · ${Number(fid.honorarios?.monto_periodico || 0).toLocaleString()} {fid.honorarios?.periodicidad}
                  </dd>
                  <dt className="text-slate-500">Creado</dt><dd className="font-medium text-slate-800">{String(fid.created_at || "").slice(0, 10)} · {fid.created_by}</dd>
                </dl>
                {fid.notas_internas && (
                  <p className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-600">{fid.notas_internas}</p>
                )}
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Historial de estados</p>
                <ol className="space-y-2.5">
                  {[...(fid.historial_estados || [])].reverse().map((h, i) => (
                    <li key={i} className="border-l-2 border-slate-200 pl-3 text-xs">
                      <p className="font-medium text-slate-800">{h.de ? `${ESTADOS_FID[h.de]?.label || h.de} → ` : ""}{ESTADOS_FID[h.a]?.label || h.a}</p>
                      <p className="text-slate-500">{String(h.fecha || "").slice(0, 16).replace("T", " ")} · {h.usuario}{h.motivo ? ` · ${h.motivo}` : ""}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="partes" className="mt-4">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 p-4">
                <p className="text-sm font-semibold text-slate-800">Partes del fideicomiso (referencias al expediente — sin recaptura de datos)</p>
                {puedePartes && !terminal && (
                  <Button data-testid="fid-btn-alta-parte" size="sm" onClick={() => setAlta(true)}>
                    <UserPlus className="mr-1.5 h-4 w-4" /> Registrar parte
                  </Button>
                )}
              </div>
              {(fid.partes || []).length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400">Sin partes registradas. El fiduciario es la propia entidad; registra fideicomitentes, fideicomisarios y el ejecutor independiente.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-4 py-2.5">Persona</th><th className="px-4 py-2.5">RFC</th>
                      <th className="px-4 py-2.5">Rol</th><th className="px-4 py-2.5">% Derechos</th>
                      <th className="px-4 py-2.5">Prelación</th><th className="px-4 py-2.5">PLD</th>
                      <th className="px-4 py-2.5">Estatus</th><th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {(fid.partes || []).map((p) => (
                      <tr key={p.id} className="border-b border-slate-50" data-testid={`fid-parte-${p.id}`}>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-slate-800">{p.persona?.nombre || p.persona?.razon_social || p.persona_id}</p>
                          {p.advertencia_conyugal?.aplica && (
                            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-600">
                              <AlertTriangle className="h-3 w-3" /> {p.advertencia_conyugal.texto}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{p.persona?.rfc || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-600">{ROL_PARTE_LABEL[p.rol] || p.rol}</td>
                        <td className="px-4 py-2.5 text-slate-600">{p.porcentaje_derechos != null ? `${p.porcentaje_derechos}%` : "—"}</td>
                        <td className="px-4 py-2.5 text-slate-600">{p.orden_prelacion || "—"}</td>
                        <td className="px-4 py-2.5">
                          {p.screening_ref ? (
                            p.screening_ref.lpb ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">LPB</span>
                            : p.screening_ref.screening_pendiente ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Pendiente</span>
                            : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{p.screening_ref.pep ? "PEP · resuelto" : "Resuelto"}</span>
                          ) : <span className="text-[11px] text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.estatus === "activa" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {p.estatus}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {puedePartes && !terminal && (
                            <Button data-testid={`fid-parte-toggle-${p.id}`} size="sm" variant="outline"
                              onClick={() => bajaParte(p)}>
                              {p.estatus === "activa" ? "Dar de baja" : "Reactivar"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="patrimonio" className="mt-4">
            <PatrimonioTab fid={fid} reload={load} puedeEscribir={esAdmin} />
          </TabsContent>

          <TabsContent value="creditos" className="mt-4">
            <CreditosCoberturaTab fid={fid} reload={load} puedeEscribir={esAdmin} />
          </TabsContent>

          <TabsContent value="ejecucion" className="mt-4">
            <EjecucionTab fid={fid} reload={load} puedeEjecutar={puedePartes} />
          </TabsContent>

          <TabsContent value="contabilidad" className="mt-4">
            <ContabilidadTab fid={fid} puedeEscribir={["admin_entidad", "oficial_cumplimiento", "contabilidad_fiscal"].includes(user?.role)} />
          </TabsContent>

          <TabsContent value="documentos" className="mt-4">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <p className="mb-4 text-sm font-semibold text-slate-800">Checklist documental — Fase 1A (solo PDF, cifrado + SHA-256)</p>
              <div className="space-y-3">
                {(fid.checklist || []).map((c) => (
                  <div key={c.clave} className="flex items-center justify-between rounded-md border border-slate-100 p-3" data-testid={`fid-doc-${c.clave}`}>
                    <div className="flex items-center gap-2.5">
                      {c.cargado ? <FileCheck2 className="h-4 w-4 text-emerald-600" /> : <Upload className="h-4 w-4 text-slate-400" />}
                      <div>
                        <p className="text-sm font-medium text-slate-800">{c.label}{c.obligatorio && <span className="ml-1 text-red-500">*</span>}</p>
                        <p className="text-[11px] text-slate-400">{c.cargado ? "Cargado y vinculado" : c.obligatorio ? "Obligatorio para constituir" : "Opcional"}</p>
                      </div>
                    </div>
                    {puedeDocs && !terminal && (
                      <label className="cursor-pointer">
                        <input type="file" accept="application/pdf" className="hidden"
                          data-testid={`fid-doc-upload-${c.clave}`}
                          onChange={(e) => subirDocumento(c.clave, e.target.files?.[0])} />
                        <span className="inline-flex items-center rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                          {subiendo === c.clave ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                          {c.cargado ? "Reemplazar" : "Cargar PDF"}
                        </span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
              {(fid.documentos || []).length > 0 && (
                <div className="mt-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Documentos vinculados</p>
                  <ul className="space-y-1.5">
                    {fid.documentos.map((d) => (
                      <li key={d.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs">
                        <span className="text-slate-700">
                          {d.documento?.nombre_archivo || d.documento_id} · {d.tipo_canonico} · {d.cargado_fecha}
                          {d.documento?.hash_sha256 && <span className="ml-2 text-slate-400">sha256:{d.documento.hash_sha256.slice(0, 12)}…</span>}
                        </span>
                        <Button data-testid={`fid-doc-descargar-${d.id}`} size="sm" variant="ghost" onClick={() => descargar(d.documento_id)}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogo de transición */}
      <Dialog open={!!trans} onOpenChange={(v) => { if (!v) setTrans(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="fid-dialog-transicion">
          <DialogHeader>
            <DialogTitle className="font-heading">Transición de estado → {ESTADOS_FID[trans]?.label || trans}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {trans === "extinguido" && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Causa de extinción (Art. 392 LGTOC) *</label>
                <Select value={causa} onValueChange={setCausa}>
                  <SelectTrigger data-testid="fid-transicion-causa"><SelectValue placeholder="Selecciona la causa" /></SelectTrigger>
                  <SelectContent>
                    {CAUSAS_EXTINCION.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Motivo (queda en bitácora)</label>
              <Textarea data-testid="fid-transicion-motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrans(null)}>Cancelar</Button>
            <Button data-testid="fid-transicion-confirmar" onClick={ejecutarTransicion}
              disabled={busy || (trans === "extinguido" && !causa)}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Confirmar transición
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de alta de parte */}
      <Dialog open={alta} onOpenChange={(v) => { if (!v) { setAlta(false); setSel(null); setBusq(""); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="fid-dialog-parte">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Landmark className="h-5 w-5" /> Registrar parte</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="rounded-md bg-slate-50 p-2.5 text-[11px] text-slate-500">
              Las partes se REFERENCIAN desde el expediente de clientes: no se vuelve a capturar ningún dato personal.
              Si la persona no existe, dala de alta primero por el flujo normal de KYC.
            </p>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Persona del expediente *</label>
              {sel ? (
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-800">{sel.nombre || sel.razon_social} <span className="text-xs text-slate-500">({sel.rfc || "sin RFC"})</span></span>
                  <Button size="sm" variant="ghost" data-testid="fid-parte-cambiar-persona" onClick={() => setSel(null)}>Cambiar</Button>
                </div>
              ) : (
                <>
                  <Input data-testid="fid-parte-buscar" placeholder="Busca por nombre, razón social o RFC…"
                    value={busq} onChange={(e) => setBusq(e.target.value)} />
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-100">
                    {personas.map((p) => (
                      <button key={p.id} data-testid={`fid-parte-persona-${p.id}`}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => setSel(p)}>
                        <span className="text-slate-800">{p.nombre || p.razon_social}</span>
                        <span className="text-xs text-slate-400">{p.rfc || ""}{p.bloqueado_lpb ? " · LPB" : ""}</span>
                      </button>
                    ))}
                    {personas.length === 0 && <p className="p-3 text-center text-xs text-slate-400">Sin resultados</p>}
                  </div>
                </>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Rol de la parte *</label>
              <Select value={rol} onValueChange={setRol}>
                <SelectTrigger data-testid="fid-parte-rol"><SelectValue placeholder="Selecciona el rol" /></SelectTrigger>
                <SelectContent>
                  {ROLES_PARTE.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-slate-400">El fiduciario no es seleccionable: en créditos propios es la propia entidad.</p>
            </div>
            {ROLES_FIDEICOMISARIO.includes(rol) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">% de derechos</label>
                  <Input data-testid="fid-parte-pct" type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Orden de prelación</label>
                  <Input data-testid="fid-parte-prelacion" type="number" min="1" value={prelacion} onChange={(e) => setPrelacion(e.target.value)} />
                </div>
              </div>
            )}
            {rol === "ejecutor_independiente" && (
              <label className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-slate-700">
                <Checkbox data-testid="fid-parte-atestacion" checked={atesta} onCheckedChange={(v) => setAtesta(!!v)} />
                <span>
                  <b>Atestación de independencia (Art. 395 LGTOC):</b> el capital, las compras y los ingresos del
                  ejecutor no están vinculados en más del 10% con las partes del crédito garantizado.
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlta(false)}>Cancelar</Button>
            <Button data-testid="fid-parte-registrar" onClick={registrarParte}
              disabled={busy || !sel || !rol || (rol === "ejecutor_independiente" && !atesta)}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Registrar parte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo estado civil pendiente (dato vive en el CLIENTE) */}
      <Dialog open={!!ecDialog} onOpenChange={(v) => { if (!v) setEcDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="fid-dialog-estado-civil">
          <DialogHeader>
            <DialogTitle className="font-heading">Completar expediente — estado civil</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-600">
              <b>{ecDialog?.nombre}</b> es fideicomitente persona física y su expediente no tiene estado civil.
              El dato se guarda EN EL CLIENTE (fuente única); el fideicomiso solo lo lee.
            </p>
            <Select value={ec} onValueChange={setEc}>
              <SelectTrigger data-testid="fid-ec-select"><SelectValue placeholder="Estado civil" /></SelectTrigger>
              <SelectContent>
                {ESTADO_CIVIL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {ec.startsWith("casado") && (
              <Input data-testid="fid-ec-regimen" placeholder="Régimen matrimonial (opcional, texto del acta)"
                value={regimen} onChange={(e) => setRegimen(e.target.value)} />
            )}
            {ec === "casado_sociedad_conyugal" && (
              <p className="flex items-start gap-1.5 rounded-md bg-amber-50 p-2.5 text-[11px] text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Se requerirá consentimiento del cónyuge para la aportación de bienes (Art. 384 LGTOC). No bloquea en esta fase.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEcDialog(null)}>Después</Button>
            <Button data-testid="fid-ec-guardar" onClick={guardarEstadoCivil} disabled={busy || !ec}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar en el expediente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
